import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, Row } from "@tanstack/react-table";
import { IconSearch } from "@tabler/icons-react";
import useSelectColumn from "components/Table/useSelectColumn";
import { searchStore } from "features/ProductSearch/SearchStore";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import MappingStatus from "features/MappingEditor/components/MappingStatus";
import useDynamicTable from "features/DynamicTable/useDynamicTable";
import useKeyPressed from "features/DynamicTable/components/useKeyPressed";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

export const COLUMNS_GROUPS = {
  infoColumns: "infoColumns",
  groupingColumns: "groupingColumns",
};

const useTableColumns = () => {
  const { t } = useTranslation();
  const informationalColumnsKeys =
    dynamicTableStore.filterStore.getcurrentGroupKeys(
      COLUMNS_GROUPS.infoColumns
    );
  const keyboard = useKeyPressed();

  const { headerCell, columnCell, objectCountCell } = useDynamicTable();

  const informationalColumns = () => {
    return {
      id: COLUMNS_GROUPS.infoColumns,
      header: () =>
        headerCell(
          t(`mappingEditor.${COLUMNS_GROUPS.infoColumns}`),
          "",
          false,
          true
        ),
      columns: dynamicTableStore.dynamicColumns
        .filter((col) => informationalColumnsKeys.includes(col.key_name))
        .map((col) => columnCell(false, col)),
      headerClassName: "border-r-0",
    };
  };

  const groupingColumns = () => {
    const visibleKeys = dynamicTableStore.filterStore.validGroupByRules.map(
      (item) => item.column_key
    );

    const columns = dynamicTableStore.dynamicColumns
      .filter((col) => visibleKeys.includes(col.key_name))
      .map((col) => columnCell(true, col));

    return {
      id: COLUMNS_GROUPS.groupingColumns,
      header: () =>
        headerCell(
          t(`mappingEditor.${COLUMNS_GROUPS.groupingColumns}`),
          "text-white",
          true
        ),
      columns: [objectCountCell(), ...columns],
      headerClassName: "bg-gray-500 text-white",
    };
  };

  function onClickMappingCell(e: React.MouseEvent, row: Row<unknown>) {
    e.stopPropagation();
    MEStore.setCurrentRow(row);
    !MEStore.openProductDetailsPopup.open &&
      searchStore.setOpenProductSearch({ state: true });
  }

  const onSelectAllClicked = (checked: boolean) => {
    MEStore.setSelectAllChecked(checked);
  };

  const onRowChecked = (row_index: number) => {
    if (MEStore.keyPressed != "Shift") {
      MEStore.setSelectedStartIndex(row_index);
    } else if (
      MEStore.selectedStartIndex != null &&
      MEStore.keyPressed === "Shift"
    ) {
      keyboard.onPressedShift(row_index);
    }
  };

  const selectCell = () => {
    return {
      id: "selectGroup",
      header: () => <div className="w-[14px] z-4"></div>,
      disableResizing: true,
      size: 32,
      maxWidth: 32,
      maxSize: 32,
      columns: [useSelectColumn(onSelectAllClicked, onRowChecked)],
      isSticky: true,
      enableSorting: false,
      stickyClass: "z-2",
    };
  };

  const mappingCell = () => ({
    id: "mappingGroup",
    header: () => <></>,
    columns: [
      {
        id: "mappingCell",
        header: () => t("mapping.objectMapping"),
        cell: (info: { row: Row<unknown> }) => (
          <div
            className="flex justify-between items-center cursor-pointer group/mapp"
            test-id={`manualmapping_row_${info.row.index}`}
          >
            <MappingStatus row={info.row} />
            <IconSearch className="w-5 transition stroke-gray-500 group-hover/mapp:stroke-gray-500" />
          </div>
        ),
        onClick: (e: React.MouseEvent, row: Row<unknown>) =>
          onClickMappingCell(e, row),
        enableColumnFilter: false,
        size: 340,
        minWidth: 340,
        maxSize: 340,
        maxWidth: 340,
        disableResizing: true,
        isSticky: true,
        stickyClass:
          "right-0 left-auto shadow-[-1px_0_4px_-2px_rgba(0,0,0,0.25)] border-l",
      },
    ],
    isSticky: true,
    size: 340,
    minWidth: 340,
    maxSize: 340,
    maxWidth: 340,
    stickyClass:
      "right-0 left-auto shadow-[-1px_0_4px_-2px_rgba(0,0,0,0.25)] border-l",
  });

  return useMemo<ColumnDef<{ [key: string]: string }>[]>(
    () => [
      selectCell(),
      ...(dynamicTableStore.filterStore.hasGrouping()
        ? [groupingColumns()]
        : []),
      ...(informationalColumnsKeys.length ? [informationalColumns()] : []),
      mappingCell(),
    ],
    [
      dynamicTableStore.dynamicColumns.length,
      dynamicTableStore.visibleColumnProperties.length,
      dynamicTableStore.filterStore.validGroupByRules.length,
    ]
  );
};

export default useTableColumns;
